import '../css/Filters.css';
import fil from '../data/filter.json';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faClose } from '@fortawesome/free-solid-svg-icons';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config/api';

function Filters({ onFiltersChange, companies = [], onRefreshFilters }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [availableYears, setAvailableYears] = useState([]);
    const [availableMonths, setAvailableMonths] = useState([]);
    
    const [selectedFilters, setSelectedFilters] = useState({
        Company: '', // Sarà popolato dinamicamente quando le companies vengono caricate
        View: 'Year',
        Year: '2025',
        Month: 'select..',
        'Compare With': 'Previous Year'  // Cambiato da 'select..' a 'Previous Year' per essere coerente
    });

    // Notifica il componente padre quando i filtri cambiano
    useEffect(() => {
        console.log('Filters useEffect - selectedFilters cambiati:', selectedFilters);
        if (onFiltersChange) {
            console.log('Filters chiama onFiltersChange con:', selectedFilters);
            onFiltersChange(selectedFilters);
        } else {
            console.log('onFiltersChange non disponibile');
        }
    }, [selectedFilters, onFiltersChange]);

    // Gestisce il cambio delle companies dall'API
    useEffect(() => {
        console.log('Filters useEffect - companies cambiate:', companies);
        if (companies && companies.length > 0) {
            // Cerca la company con IsDefault = true (nota: la proprietà è case-sensitive)
            const defaultCompany = companies.find(company => company.isDefault === true);
            
            if (defaultCompany) {
                // Se trova una company di default, la seleziona
                console.log('Company di default trovata:', defaultCompany.name);
                setSelectedFilters(prev => ({ ...prev, Company: defaultCompany.name }));
            } else if (companies.length > 0) {
                // Fallback: se non c'è una company di default, usa la prima
                console.log('Usa prima company disponibile:', companies[0].name);
                setSelectedFilters(prev => ({ ...prev, Company: companies[0].name }));
            }
        }
    }, [companies]);

    // Restituisce il mese cronologicamente più recente (anno + mese), non il numero di mese più alto
    const getLatestMonthFromList = (monthsList) => {
        if (!monthsList || monthsList.length === 0) return null;
        return monthsList.reduce((best, m) => {
            if (!best) return m;
            if (m.year > best.year) return m;
            if (m.year === best.year && (m.monthNumber ?? 0) > (best.monthNumber ?? 0)) return m;
            return best;
        }, null);
    };

    // Funzione per caricare gli anni e mesi disponibili
    const fetchAvailableYearsAndMonths = async () => {
        try {
            const response = await axios.get(buildApiUrl('import-excel/available-years-months'));
            if (response.data.status === 'OK') {
                setAvailableYears(response.data.years);
                setAvailableMonths(response.data.months);
                
                if (response.data.years.length > 0 && response.data.months.length > 0) {
                    // Anno e mese più recenti in senso cronologico (anno più recente → mese più recente)
                    const latestMonth = getLatestMonthFromList(response.data.months);
                    if (latestMonth) {
                        setSelectedFilters(prev => ({
                            ...prev,
                            Year: latestMonth.year.toString(),
                            Month: latestMonth.value
                        }));
                        console.log(`Mese più recente selezionato (anno + mese): ${latestMonth.value} ${latestMonth.year}`);
                    }
                }
            }
        } catch (error) {
            console.error('Errore nel caricamento degli anni e mesi disponibili:', error);
        }
    };

    // Carica gli anni e mesi disponibili al mount del componente
    useEffect(() => {
        fetchAvailableYearsAndMonths();
    }, []);

    // Ascolta le richieste di refresh dei filtri dal componente padre
    useEffect(() => {
        if (onRefreshFilters) {
            // Crea una funzione che può essere chiamata dal padre
            onRefreshFilters.current = fetchAvailableYearsAndMonths;
        }
    }, [onRefreshFilters]);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
    };

    const handleFilterChange = (filterTitle, value) => {
        console.log('handleFilterChange chiamato:', { filterTitle, value });
        console.log('Stato attuale selectedFilters:', selectedFilters);
        
        const newFilters = { ...selectedFilters, [filterTitle]: value };
        
        // Reset year e month a ogni cambio View: anno + mese più recenti in senso cronologico
        if (filterTitle === 'View') {
            const latestMonth = getLatestMonthFromList(availableMonths);
            if (latestMonth) {
                newFilters.Year = latestMonth.year.toString();
                newFilters.Month = latestMonth.value;
                console.log(`View cambiata: mese più recente ${latestMonth.value} ${latestMonth.year}`);
            } else {
                newFilters.Year = availableYears.length > 0 ? availableYears[0].toString() : '2025';
                newFilters.Month = 'select..';
            }
        }
        
        // Reset month quando cambia l'anno e seleziona il mese più recente per quell'anno
        if (filterTitle === 'Year') {
            // Trova il mese più recente per l'anno selezionato
            const selectedYear = parseInt(value);
            const monthsForYear = availableMonths.filter(month => month.year === selectedYear);
            
            if (monthsForYear.length > 0) {
                // Il mese più recente è l'ultimo nell'array ordinato cronologicamente
                const latestMonthForYear = monthsForYear[monthsForYear.length - 1];
                newFilters.Month = latestMonthForYear.value;
                console.log(`Mese più recente per l'anno ${selectedYear}: ${latestMonthForYear.value}`);
            } else {
                newFilters.Month = 'select..';
            }
        }
        
        console.log('Nuovi filtri prima di setSelectedFilters:', newFilters);
        setSelectedFilters(newFilters);
        console.log('Filters updated:', newFilters); // Debug
    };


    // Funzione per ottenere le opzioni dinamiche per Company
    const getCompanyOptions = () => {
        if (companies && companies.length > 0) {
            // Ordina le companies mettendo quella con IsDefault = true per prima
            const sortedCompanies = [...companies].sort((a, b) => {
                const aIsDefault = a.isDefault === true || a.IsDefault === true;
                const bIsDefault = b.isDefault === true || b.IsDefault === true;
                
                if (aIsDefault) return -1;
                if (bIsDefault) return 1;
                return a.name.localeCompare(b.name);
            });
            
            return sortedCompanies.map(company => ({ label: company.name }));
        }
        // Fallback alle opzioni statiche se non ci sono companies dall'API
        const staticOptions = fil.find(f => f.title === 'Company')?.options || [];
        return staticOptions;
    };

    // Funzione per ottenere le opzioni dinamiche per Year
    const getYearOptions = () => {
        if (availableYears.length > 0) {
            return availableYears.map(year => ({ label: year.toString() }));
        }
        // Fallback alle opzioni statiche se non ci sono anni dall'API
        const staticOptions = fil.find(f => f.title === 'Year')?.options || [];
        return staticOptions;
    };

    // Funzione per ottenere le opzioni dinamiche per Month
    const getMonthOptions = () => {
        if (availableMonths.length > 0 && selectedFilters.Year) {
            // Filtra i mesi per l'anno selezionato
            const selectedYear = parseInt(selectedFilters.Year);
            const monthsForYear = availableMonths.filter(month => month.year === selectedYear);
            return monthsForYear.map(month => ({ label: month.value }));
        }
        // Fallback alle opzioni statiche se non ci sono mesi dall'API
        const staticOptions = fil.find(f => f.title === 'Month')?.options || [];
        return staticOptions;
    };

    return (
        <div className="filters-container">
            <button
                className="hamburger-btn"
                onClick={toggleMenu}
                aria-label="Open filters menu"
            >
                <FontAwesomeIcon icon={faBars} size="lg" />
                <span className="spantbn">Choose a filter</span>
            </button>

            <div className={`filter ${isMenuOpen ? 'filter-open' : ''}`}>
                <button
                    className="close-btn"
                    onClick={closeMenu}
                    aria-label="Close filters menu"
                >
                    <FontAwesomeIcon icon={faClose} size="lg" />
                </button>

                {fil.map((filter) => {
                    // Usa filter.title come chiave univoca
                    const filterKey = filter.title;
                    // I filtri Year e Month sono ora sempre abilitati
                    const disabled = false;

                    console.log(`Filter: ${filterKey}, Disabled: ${disabled}, Current value: ${selectedFilters[filterKey]}`); // Debug

                    // Ottieni le opzioni appropriate per il filtro
                    let filterOptions = filter.options;
                    if (filterKey === 'Company') {
                        filterOptions = getCompanyOptions();
                    } else if (filterKey === 'Year') {
                        filterOptions = getYearOptions();
                    } else if (filterKey === 'Month') {
                        filterOptions = getMonthOptions();
                    }

                    return (
                        <div className="filterDiv" key={filter.title}>
                            <label className="filterLabel">
                                {filter.title}
                            </label>
                            <select
                                value={selectedFilters[filterKey] || filter.holder}
                                onChange={(e) => handleFilterChange(filterKey, e.target.value)}
                                className={`select ${disabled ? 'disabled' : ''}`}
                                disabled={disabled}
                            >
                                {filterOptions.map((option, optionIndex) => (
                                    <option key={optionIndex} value={option.label} disabled={option.disabled || false}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    );
                })}
            </div>

            {isMenuOpen && (
                <div className="filter-overlay" onClick={closeMenu}></div>
            )}
        </div>
    );
}

export default Filters;